import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card, Tag, Typography, Space, Button, Empty, message, Spin, Popconfirm,
  Tabs, Tooltip, Avatar, Alert,
} from 'antd';
import {
  ThunderboltOutlined, CheckCircleOutlined, CloseCircleOutlined,
  DeleteOutlined, ReloadOutlined, RobotOutlined, ClockCircleOutlined,
  FacebookOutlined, MailOutlined, FileTextOutlined, GoogleOutlined,
} from '@ant-design/icons';
import { aiSalesAPI } from '../services/api';

const { Text, Paragraph, Title } = Typography;

const ACTION_META = {
  fb_post:           { label: 'FB 貼文',  icon: <FacebookOutlined />, color: 'geekblue' },
  email_blast:       { label: 'Email 群發', icon: <MailOutlined />,    color: 'blue' },
  blog_post:         { label: 'Blog 文章', icon: <FileTextOutlined />, color: 'cyan' },
  google_ads_create: { label: 'Google Ads', icon: <GoogleOutlined />,  color: 'orange' },
  video_generate:    { label: 'Video',     icon: <ThunderboltOutlined />, color: 'purple' },
};

export default function BotProposalsPage() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [actingId, setActingId] = useState(null);

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aiSalesAPI.listProposals(statusFilter);
      const body = res.data ?? res;
      setProposals(body.proposals || []);
    } catch (e) {
      message.error(`載入失敗：${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchProposals();
    const id = setInterval(fetchProposals, 60_000);
    return () => clearInterval(id);
  }, [fetchProposals]);

  const handleApprove = async (id) => {
    setActingId(id);
    try {
      await aiSalesAPI.approveProposal(id);
      message.success('已核准，背景執行中');
      fetchProposals();
    } catch (e) {
      message.error(`核准失敗：${e.message}`);
    } finally { setActingId(null); }
  };
  const handleReject = async (id) => {
    setActingId(id);
    try {
      await aiSalesAPI.rejectProposal(id);
      message.success('已拒絕');
      fetchProposals();
    } catch (e) {
      message.error(`拒絕失敗：${e.message}`);
    } finally { setActingId(null); }
  };
  const handleDelete = async (id) => {
    setActingId(id);
    try {
      await aiSalesAPI.deleteProposal(id);
      message.success('已刪除');
      fetchProposals();
    } catch (e) {
      message.error(`刪除失敗：${e.message}`);
    } finally { setActingId(null); }
  };

  const groupedByBot = useMemo(() => {
    const m = new Map();
    for (const p of proposals) {
      if (!m.has(p.bot_id)) m.set(p.bot_id, { bot_id: p.bot_id, bot_name: p.bot_name, bot_alayaess_agent: p.bot_alayaess_agent, items: [] });
      m.get(p.bot_id).items.push(p);
    }
    return Array.from(m.values());
  }, [proposals]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <ThunderboltOutlined /> Bot 行動提議審核
          </Title>
          <Text type="secondary">
            非 manual 模式的 bot 自動產生的建議會堆在這。Approve 才會真的發 FB / Email / blog。每 60s 自動重整。
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchProposals} loading={loading}>重新整理</Button>
      </div>

      <Tabs
        activeKey={statusFilter}
        onChange={setStatusFilter}
        items={[
          { key: 'pending', label: `待審 (${statusFilter === 'pending' ? proposals.length : '...'})` },
          { key: 'fired', label: '已執行' },
          { key: 'rejected', label: '已拒絕' },
          { key: 'all', label: '全部' },
        ]}
      />

      <Spin spinning={loading}>
        {proposals.length === 0 ? (
          <Empty description={
            statusFilter === 'pending'
              ? '目前沒有待審的提議。請去 AI 業務競賽編輯 bot，把 autonomy_mode 改成 inbox / daily_plan / loop，等下次 tick (30 分鐘內) 就會有建議出現。'
              : '此狀態下沒有提議'
          } />
        ) : (
          groupedByBot.map(grp => (
            <Card
              key={grp.bot_id}
              size="small"
              title={
                <Space>
                  <Avatar size={28} icon={<RobotOutlined />} style={{ background: '#3b82f6' }} />
                  <span style={{ fontWeight: 600 }}>{grp.bot_name}</span>
                  {grp.bot_alayaess_agent && <Tag>AlayaEss: {grp.bot_alayaess_agent}</Tag>}
                  <Tag color="default">{grp.items.length} 個提議</Tag>
                </Space>
              }
              style={{ marginBottom: 16 }}
              extra={
                <Link to={`/ai-sales/bots/${grp.bot_id}/chat`}>
                  <Button size="small">💬 進對話頁</Button>
                </Link>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {grp.items.map(p => {
                  const meta = ACTION_META[p.action_type] || { label: p.action_type, icon: null, color: 'default' };
                  return (
                    <Card key={p.id} size="small" bodyStyle={{ padding: 10 }}>
                      <Space style={{ marginBottom: 8 }}>
                        <Tag color={meta.color} icon={meta.icon}>{meta.label}</Tag>
                        {p.scheduled_at && (
                          <Tag icon={<ClockCircleOutlined />}>
                            排程 {new Date(p.scheduled_at).toLocaleString('zh-TW')}
                          </Tag>
                        )}
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          建立於 {new Date(p.created_at).toLocaleString('zh-TW')}
                        </Text>
                      </Space>

                      {p.reasoning && (
                        <Alert
                          type="info" showIcon={false}
                          message={<Text style={{ fontSize: 13 }}>💡 {p.reasoning}</Text>}
                          style={{ marginBottom: 8, padding: '6px 10px' }}
                        />
                      )}

                      {p.preview_content && (
                        <Paragraph
                          ellipsis={{ rows: 4, expandable: true, symbol: '展開' }}
                          style={{ marginBottom: 8, background: '#f9fafb', padding: 8, borderRadius: 4, whiteSpace: 'pre-wrap' }}
                        >
                          {p.preview_content}
                        </Paragraph>
                      )}

                      <Space>
                        {p.status === 'pending' && (
                          <>
                            <Popconfirm title="核准並執行？" onConfirm={() => handleApprove(p.id)}>
                              <Button
                                type="primary" size="small" icon={<CheckCircleOutlined />}
                                loading={actingId === p.id}
                              >核准並執行</Button>
                            </Popconfirm>
                            <Button
                              size="small" icon={<CloseCircleOutlined />}
                              onClick={() => handleReject(p.id)}
                              loading={actingId === p.id}
                            >拒絕</Button>
                          </>
                        )}
                        {p.status !== 'pending' && (
                          <Tag color={
                            p.status === 'fired' ? 'success' :
                            p.status === 'rejected' ? 'red' :
                            p.status === 'approved' ? 'blue' : 'default'
                          }>{p.status}</Tag>
                        )}
                        <Popconfirm title="從佇列中刪除？" onConfirm={() => handleDelete(p.id)}>
                          <Tooltip title="刪除（不會 fire）">
                            <Button size="small" danger icon={<DeleteOutlined />} loading={actingId === p.id} />
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    </Card>
                  );
                })}
              </Space>
            </Card>
          ))
        )}
      </Spin>
    </div>
  );
}
